import { useState, useMemo, useEffect } from 'react';
import { LineChart, BarChart } from '@mui/x-charts';
import { Skeleton } from '@mui/material';
import { IconChartLine, IconChartBar } from '@tabler/icons-react';

import { ButtonGroup, Text } from '@fragments';

import '../styles.scss';
import './styles.scss';

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const GRANULARITY_LABELS = {
    hour: 'hora',
    day: 'dia',
    week: 'semana',
    month: 'mês',
    year: 'ano',
};

// Upper bound on how many x-axis labels we let MUI render. More than this
// crowds the axis and forces MUI's auto-decimation, which is uneven for
// band scales with repeating labels (e.g. hourly buckets across many days).
const MAX_VISIBLE_LABELS = 12;

function pad(n) {
    return String(n).padStart(2, '0');
}

function bucketLabel(point, granularityType, hourSpansDays) {
    if (!point?.date?.from) return '';
    const d = new Date(point.date.from.replace(' ', 'T'));
    switch (granularityType) {
        case 'hour':
            // Across multiple days, the tick interval thins labels down to
            // midnight buckets — show those as dates so the day boundary is
            // legible. Non-midnight buckets keep the bare hour for the
            // single-day case.
            if (hourSpansDays) return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
            return `${pad(d.getHours())}h`;
        case 'day':
        case 'week':
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
        case 'month':
            return `${MONTH_LABELS[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}`;
        case 'year':
            return String(d.getFullYear());
        default:
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
    }
}

// Picks which bucket indexes get an x-axis label. For hour granularity
// spanning many days, snap to midnight indexes so labels land on day
// boundaries; otherwise stride evenly across the data.
function computeTickLabelInterval(points, granularityType) {
    if (points.length <= MAX_VISIBLE_LABELS) return undefined;

    if (granularityType === 'hour') {
        const midnights = [];
        for (let i = 0; i < points.length; i++) {
            const from = points[i]?.date?.from;
            if (!from) continue;
            if (new Date(from.replace(' ', 'T')).getHours() === 0) midnights.push(i);
        }
        if (midnights.length > 1) {
            const stride = Math.max(1, Math.ceil(midnights.length / MAX_VISIBLE_LABELS));
            const visible = new Set();
            for (let i = 0; i < midnights.length; i += stride) visible.add(midnights[i]);
            return (_v, idx) => visible.has(idx);
        }
    }

    const stride = Math.max(1, Math.ceil(points.length / MAX_VISIBLE_LABELS));
    return (_v, idx) => idx % stride === 0;
}

function bucketTooltipLabel(point, granularityType, amount) {
    if (!point?.date?.from) return '';
    const from = new Date(point.date.from.replace(' ', 'T'));
    const to = point?.date?.to ? new Date(point.date.to.replace(' ', 'T')) : null;
    const unit = GRANULARITY_LABELS[granularityType] || 'período';

    if (amount > 1 && to) {
        return `${pad(from.getDate())}/${pad(from.getMonth() + 1)} — ${pad(to.getDate())}/${pad(to.getMonth() + 1)}`;
    }
    if (granularityType === 'month') {
        return `${MONTH_LABELS[from.getMonth()]} de ${from.getFullYear()}`;
    }
    if (granularityType === 'year') {
        return String(from.getFullYear());
    }
    if (granularityType === 'hour') {
        return `${pad(from.getDate())}/${pad(from.getMonth() + 1)} ${pad(from.getHours())}h`;
    }
    return `${pad(from.getDate())}/${pad(from.getMonth() + 1)}/${from.getFullYear()} (${unit})`;
}

function formatMoneyCompact(value) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `R$${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `R$${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return `R$${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatMoneyFull(value) {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumberCompact(value) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(value);
}

const VIEW_OPTIONS = [
    { value: 'line', icon: <IconChartLine size={16} /> },
    { value: 'bar', icon: <IconChartBar size={16} /> },
];

// MUI x-charts feeds the series color into d3-color's brighter() to compute
// area gradients. d3-color can't parse `var(...)`, so we resolve the CSS var
// to a hex string at runtime via getComputedStyle.
function useResolvedCssColor(varName) {
    const [color, setColor] = useState(() => {
        if (typeof window === 'undefined') return undefined;
        return getComputedStyle(document.documentElement)
            .getPropertyValue(varName).trim() || undefined;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const value = getComputedStyle(document.documentElement)
            .getPropertyValue(varName)
            .trim();
        if (value) setColor(value);
    }, [varName]);
    return color;
}

// Axis/grid/legend colors live in `_charts/styles.scss` (CSS rules with
// `!important` so they beat MUI's inline styles). The only chart-wide sx
// override we still need here is hiding the legend — single series.
const chartSx = {
    '.MuiChartsLegend-root': {
        display: 'none',
    },
};

export default function TimeSeriesChart({
    title,
    icon,
    timeSeries,
    valueType = 'number',  // 'number' | 'money'
    unitSingular = 'item',
    unitPlural = 'itens',
    report,
    reportLoading,
    defaultMode = 'bar',
    colorVar = '--chart-1',
}) {
    const [mode, setMode] = useState(defaultMode);
    const seriesColor = useResolvedCssColor(colorVar);

    const points = timeSeries?.data || [];
    const granularityType = timeSeries?.granularity?.type || 'day';
    const granularityAmount = timeSeries?.granularity?.amount || 1;

    const isMoney = valueType === 'money';

    const { values, labels, total, tickLabelInterval } = useMemo(() => {
        const raw = points.map(p => Number(p.value) || 0);
        const vals = isMoney ? raw.map(v => v / 100) : raw;
        const hourSpansDays = granularityType === 'hour' && points.length > 24;
        const lbls = points.map(p => bucketLabel(p, granularityType, hourSpansDays));
        const sum = vals.reduce((acc, v) => acc + v, 0);
        const interval = computeTickLabelInterval(points, granularityType);
        return { values: vals, labels: lbls, total: sum, tickLabelInterval: interval };
    }, [points, granularityType, isMoney]);

    const formatShort = isMoney ? formatMoneyCompact : formatNumberCompact;

    function formatFull(value) {
        if (isMoney) return formatMoneyFull(value);
        const n = Number(value);
        const noun = n === 1 ? unitSingular : unitPlural;
        return `${n.toLocaleString('pt-BR')} ${noun}`;
    }

    const valueFormatter = (v, ctx) => {
        const idx = ctx?.dataIndex;
        const range = idx != null ? bucketTooltipLabel(points[idx], granularityType, granularityAmount) : '';
        const valueText = formatFull(v);
        return range ? `${valueText} · ${range}` : valueText;
    };

    const totalText = formatFull(total);

    return (
        <div className={`ts-chart-card${reportLoading && report ? ' ts-chart-card--reloading' : ''}`}>
            <div className="ts-chart-card__header">
                <div className="ts-chart-card__title">
                    <span className="ts-chart-card__icon">{icon}</span>
                    <Text.h4 contrast={7}>{title}</Text.h4>
                    <Text.span contrast={4} className="ts-chart-card__count">
                        · {totalText} no total
                    </Text.span>
                </div>
                <ButtonGroup
                    options={VIEW_OPTIONS}
                    value={mode}
                    onChange={setMode}
                    size="small"
                />
            </div>

            <div className="ts-chart-card__body">
                {reportLoading && !report ? (
                    <Skeleton variant="rounded" height={260} className="ts-chart-card__skeleton" />
                ) : !points.length ? (
                    <div className="ts-chart-card__empty">
                        <Text.p contrast={5}>Sem dados para o período selecionado.</Text.p>
                    </div>
                ) : mode === 'line' ? (
                    <LineChart
                        height={260}
                        skipAnimation={false}
                        series={[{
                            data: values,
                            color: seriesColor,
                            curve: 'monotoneX',
                            area: true,
                            showMark: values.length <= 24,
                            valueFormatter,
                        }]}
                        xAxis={[{ data: labels, scaleType: 'point', tickLabelInterval }]}
                        yAxis={[{ valueFormatter: formatShort }]}
                        grid={{ horizontal: true }}
                        margin={{ top: 16, bottom: 28, left: isMoney ? 56 : 36, right: 16 }}
                        sx={{
                            ...chartSx,
                            '.MuiLineElement-root': {
                                strokeWidth: 2,
                            },
                            '.MuiAreaElement-root': {
                                fill: seriesColor,
                                opacity: 0.12,
                            },
                            '.MuiMarkElement-root': {
                                stroke: seriesColor,
                                fill: 'var(--surface-10)',
                                strokeWidth: 2,
                            },
                        }}
                    />
                ) : (
                    <BarChart
                        height={260}
                        skipAnimation={false}
                        series={[{
                            data: values,
                            color: seriesColor,
                            valueFormatter,
                        }]}
                        xAxis={[{ data: labels, scaleType: 'band', tickLabelInterval }]}
                        yAxis={[{ valueFormatter: formatShort }]}
                        grid={{ horizontal: true }}
                        margin={{ top: 16, bottom: 28, left: isMoney ? 56 : 36, right: 16 }}
                        borderRadius={6}
                        slotProps={{ bar: { rx: 4 } }}
                        sx={chartSx}
                    />
                )}
            </div>
        </div>
    );
}
